/**
 * /marketing/audience — what every marketing contact IS, and why.
 *
 * Track 6, 2026-10-08. Replaces "N contacts held" with ten audience
 * states. Every tile and reason chip is a filter; every row shows the
 * evidence behind its classification; two deliberately different bulk
 * actions sit on checked rows:
 *
 *   Retire from marketing  the address is never marketed again; the CRM
 *                          lead and all history are kept.
 *   Delete garbage         PERMANENT. Only contacts the server reports as
 *                          deletion-safe, after a dry run, a typed
 *                          confirmation, and a fresh server-side re-check
 *                          of every contact. Platform admins only.
 *
 * Nothing on this screen sends email, releases a hold or lifts
 * suppression.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import MarketingShell from "./MarketingShell";
import {
  ArrowClockwise, Trash, ProhibitInset, X, MagnifyingGlass, Warning, UsersThree,
} from "@phosphor-icons/react";
import {
  apiParams, BAD_ADDRESS_CATEGORIES, chunk, deletableSelection, describeFilters, eligibleSelection,
  filtersFromSearch, isCleanSuccess, mergeResults, outcomeSummary, searchFromFilters,
} from "@/lib/audienceFilters";
import { digestOriginFromSearch } from "@/lib/digestOrigin";
import DigestOriginNote from "@/components/marketing/DigestOriginNote";

const STATE_COLORS = {
  ACTIVE: "#16A34A",
  VERIFIED: "#0055FF",
  SALES_OWNED: "#7C3AED",
  SUPPRESSED: "#525252",
  INVALID: "#DC2626",
  HISTORICALLY_ENGAGED: "#0D9488",
  NEVER_ENGAGED: "#D97706",
  STALE: "#737373",
  TERMINAL: "#404040",
  REVIEW: "#EA580C",
};

const SORTS = [
  ["lead_created", "Lead created"],
  ["last_delivery", "Last delivery"],
  ["last_engaged", "Last engagement"],
  ["last_attempt", "Last email attempt"],
  ["verified_until", "Verified until"],
  ["domain", "Domain"],
  ["company", "Company"],
  ["name", "Name"],
  ["sales_stage", "Sales stage"],
  ["lead_source", "Lead source"],
  ["reason", "Reason"],
];

const PAGE = 100;

const OVERRIDE_LABELS = { review: "Move to review", sales_owned: "Mark Sales owned", clear: "Clear override" };
const OVERRIDE_HELP = {
  review: "Puts the contacts in Review required until the override is cleared. Sends, holds and suppression are not changed.",
  sales_owned: "Shows the contacts as Sales owned and keeps them out of automatic nurture entry. Suppressed or invalid contacts cannot be moved.",
  clear: "Removes an operator override; each contact goes back to the state its evidence gives it.",
};

function errorText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

export default function AudiencePage() {
  const loc = useLocation();
  const nav = useNavigate();
  const filters = useMemo(() => filtersFromSearch(loc.search), [loc.search]);
  // Set only on a digest deep link; any filter change navigates without it.
  const origin = useMemo(() => digestOriginFromSearch(loc.search), [loc.search]);

  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [list, setList] = useState({ rows: [], total: 0, delete_eligible_ids: [],
    bad_address_removable_ids: [], test_data_deletable_ids: [] });
  const [listError, setListError] = useState("");
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState(filters.q || "");
  const [detailId, setDetailId] = useState(null);
  const [action, setAction] = useState(null); // {kind, ids, preview, results, busy, typed}

  const setFilters = useCallback((next) => {
    const qs = searchFromFilters(next);
    setOffset(0);
    setSelected([]);
    nav(`/marketing/audience${qs ? `?${qs}` : ""}`);
  }, [nav]);

  const loadSummary = useCallback(async (refresh = false) => {
    try {
      const r = await api.get("/marketing/audience/summary", { params: { refresh } });
      setSummary(r.data);
      setSummaryError("");
    } catch (e) {
      setSummaryError(errorText(e, "Could not load the audience summary. Refresh the page; if this continues, contact support."));
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/audience/contacts", { params: apiParams(filters, offset, PAGE) });
      setList(r.data);
      setListError("");
    } catch (e) {
      setListError(errorText(e, "Could not load contacts. Refresh the page; if this continues, contact support."));
    }
    setLoading(false);
  }, [filters, offset]);

  useEffect(() => { loadSummary(false); }, [loadSummary]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setSearch(filters.q || ""); }, [filters.q]);

  const refreshAll = async () => {
    await loadSummary(true);
    await loadList();
  };

  const states = summary?.states || [];
  const activeState = states.find((s) => s.state === filters.state);
  const filterWords = describeFilters(
    filters,
    Object.fromEntries(states.map((s) => [s.state, s.label])),
    Object.fromEntries((activeState?.reasons || []).map((r) => [r.reason_code, r.reason])),
  );
  const maxBatch = summary?.max_action_contacts || 50;
  const rowIds = list.rows.map((r) => r.contact_id);
  const allOnPageSelected = rowIds.length > 0 && rowIds.every((id) => selected.includes(id));
  const deletable = deletableSelection(selected, list.delete_eligible_ids);
  const removableBad = eligibleSelection(selected, list.bad_address_removable_ids);
  const deletableTest = eligibleSelection(selected, list.test_data_deletable_ids);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const togglePage = () => setSelected((s) => (allOnPageSelected
    ? s.filter((id) => !rowIds.includes(id))
    : Array.from(new Set([...s, ...rowIds]))));

  // ── Bulk actions: dry run first, then confirm ─────────────────────
  const endpoint = {
    delete: "/marketing/audience/delete-garbage", retire: "/marketing/audience/retire",
    remove_bad: "/marketing/audience/remove-bad-addresses", test_data: "/marketing/audience/delete-test-data",
  };

  // Runs batch by batch and KEEPS what earlier batches did if a later one
  // fails, so the operator always sees exactly which contacts changed.
  const runBatches = async (kind, ids, extra) => {
    const responses = [];
    const batches = chunk(ids, maxBatch);
    let error = null;
    let done = 0;
    for (const batch of batches) {
      try {
        const r = await api.post(endpoint[kind], { contact_ids: batch, ...extra });
        responses.push(r.data);
        done += batch.length;
      } catch (e) {
        error = errorText(e, "The server did not answer for this batch.");
        break;
      }
    }
    return { ...mergeResults(responses), error, notProcessed: ids.length - done };
  };

  const startAction = async (kind) => {
    const ids = { delete: deletable, remove_bad: removableBad, test_data: deletableTest }[kind] || selected;
    if (!ids.length) {
      toast.error({
        delete: "None of the selected contacts is eligible for permanent deletion.",
        remove_bad: "None of the selected contacts has a proven bad address that can be removed.",
        test_data: "None of the selected contacts is deletable Eyvo staff / test data.",
      }[kind] || "Select at least one contact.");
      return;
    }
    setAction({ kind, ids, busy: true, preview: null, results: null, typed: "", note: "" });
    const preview = await runBatches(kind, ids, { apply: false });
    if (preview.error) {
      setAction(null);
      toast.error(`The dry run failed: ${preview.error} Nothing was changed.`);
      return;
    }
    setAction({ kind, ids, busy: false, preview, results: null, typed: "", note: "" });
  };

  const confirmAction = async () => {
    if (!action) return;
    const cfg = ACTIONS[action.kind];
    const willAct = (action.preview?.results || [])
      .filter((r) => r.outcome === cfg.preview)
      .map((r) => r.contact_id);
    if (!willAct.length) return;
    setAction({ ...action, busy: true });
    const extra = cfg.confirmWord
      ? { apply: true, confirm: action.typed, note: action.note }
      : { apply: true, note: action.note };
    const results = await runBatches(action.kind, willAct, extra);
    setAction({ ...action, busy: false, results });
    setSelected([]);
    const summary = outcomeSummary(results.outcomes);
    if (results.error) {
      toast.error(`Stopped part-way: ${results.error} ${summary ? `${summary}. ` : ""}`
        + `${results.notProcessed} contact(s) were not processed. Each processed contact's result is listed.`);
    } else if (isCleanSuccess(results.outcomes)) {
      toast.success(summary);
    } else {
      toast.warning(`Finished with exceptions: ${summary}. See each contact's result.`);
    }
    await refreshAll();
  };

  const [overrideAction, setOverrideAction] = useState(null); // {kind, note, busy}

  const applyOverride = async () => {
    const { kind, note } = overrideAction;
    setOverrideAction({ ...overrideAction, busy: true });
    const responses = [];
    let error = null;
    for (const batch of chunk(selected, maxBatch)) {
      try {
        const r = await api.post("/marketing/audience/override", { contact_ids: batch, action: kind, note });
        responses.push({ results: r.data.results, outcomes: countOutcomes(r.data.results) });
      } catch (e) {
        error = errorText(e, "The server did not answer.");
        break;
      }
    }
    const merged = mergeResults(responses);
    const summary = outcomeSummary(merged.outcomes);
    if (error) {
      toast.error(`${OVERRIDE_LABELS[kind]} stopped part-way: ${error}${summary ? ` Done so far: ${summary}.` : " Nothing was changed."}`);
    } else if (isCleanSuccess(merged.outcomes)) {
      toast.success(`${OVERRIDE_LABELS[kind]}: ${summary}`);
    } else {
      toast.warning(`${OVERRIDE_LABELS[kind]}: ${summary}. Protected contacts (suppressed or invalid) cannot be overridden.`);
    }
    setOverrideAction(null);
    setSelected([]);
    await refreshAll();
  };

  return (
    <MarketingShell>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-2">
          <UsersThree size={20} className="text-[#0055FF] mt-0.5" />
          <div>
            <p className="text-sm font-bold text-[#0A0A0A]">Marketing audience</p>
            <p className="text-xs text-[#525252] max-w-3xl">
              Every marketing contact, by what it is and why. Click a count to see the exact contacts,
              open a row for the evidence, and tick rows to act on them. Nothing here sends email,
              releases a hold or lifts suppression.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {summary?.as_of && (
            <span className="text-[11px] text-[#A3A3A3]" title="Counts are recalculated at most every 5 minutes, or when you press Refresh.">
              As of {new Date(summary.as_of).toLocaleString()}
            </span>
          )}
          <button onClick={refreshAll} className="h-8 px-3 border border-[#E5E5E5] bg-white text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid="audience-refresh">
            <ArrowClockwise size={12} /> Refresh
          </button>
        </div>
      </div>

      <DigestOriginNote origin={origin} current={loading || listError ? null : list.total} />
      {summaryError && <Banner tone="error">{summaryError}</Banner>}
      {summary?.errors?.length > 0 && (
        <Banner tone="warn">
          Some evidence could not be read ({summary.errors.join(", ")}). Affected contacts are not offered for deletion.
        </Banner>
      )}

      {/* State tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="audience-tiles">
        {states.map((s) => {
          const on = filters.state === s.state;
          return (
            <button key={s.state} onClick={() => setFilters(on ? {} : { state: s.state })}
                    title={s.help}
                    className={`text-left bg-white border p-3 hover:border-[#0055FF] transition-colors ${on ? "border-[#0055FF] ring-1 ring-[#0055FF]" : "border-[#E5E5E5]"}`}
                    data-testid={`audience-tile-${s.state}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: STATE_COLORS[s.state] }}>{s.label}</div>
              <div className="text-2xl font-black tabular-nums text-[#0A0A0A]">{s.contacts.toLocaleString()}</div>
            </button>
          );
        })}
      </div>

      {/* Cross-cutting filters */}
      {summary && (
        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          <Chip on={filters.delete_eligible === true} onClick={() => setFilters(filters.delete_eligible ? {} : { delete_eligible: true })} testid="chip-delete-eligible">
            Eligible for garbage cleanup · {summary.delete_eligible.toLocaleString()}
          </Chip>
          <Chip on={filters.conflict === true} onClick={() => setFilters(filters.conflict ? {} : { conflict: true })} testid="chip-conflict">
            Still in automatic email despite Sales ownership or quarantine · {summary.sales_conflicts.toLocaleString()}
          </Chip>
          <Chip on={filters.cleanup === true} onClick={() => setFilters(filters.cleanup ? {} : { cleanup: true })} testid="chip-cleanup">
            Contacts with enrolment rows that can never send · {summary.cleanup_contacts.toLocaleString()}
          </Chip>
          <Chip on={filters.releasable === true} onClick={() => setFilters(filters.releasable ? {} : { state: "VERIFIED", releasable: true })} testid="chip-releasable">
            Verified and releasable in stages · {summary.verified_releasable.toLocaleString()}
          </Chip>
          {summary.bad_address !== undefined && (
            <Chip on={filters.bad_address === true} onClick={() => setFilters(filters.bad_address ? {} : { bad_address: true })} testid="chip-bad-address">
              Bad / undeliverable addresses · {summary.bad_address.toLocaleString()}
              {summary.bad_address_removable !== summary.bad_address ? ` (${summary.bad_address_removable.toLocaleString()} removable)` : ""}
            </Chip>
          )}
          {summary.test_data !== undefined && (
            <Chip on={filters.test_data === true} onClick={() => setFilters(filters.test_data ? {} : { test_data: true })} testid="chip-test-data">
              Eyvo staff / test data · {summary.test_data.toLocaleString()}
            </Chip>
          )}
          {summary.held_contacts !== undefined && (
            <Chip on={filters.held === true} onClick={() => setFilters(filters.held ? {} : { held: true })} testid="chip-held">
              Contacts with paused (held) enrolments · {summary.held_contacts.toLocaleString()}
            </Chip>
          )}
          {Object.keys(filters).length > 0 && (
            <button onClick={() => setFilters({})} className="text-[#0055FF] font-semibold">Show everyone</button>
          )}
        </div>
      )}

      {filters.bad_address === true && summary?.bad_address_by_category && (
        <div className="bg-white border border-[#E5E5E5] p-3 mb-3" data-testid="bad-address-panel">
          <p className="text-xs text-[#525252] mb-2">
            Real contacts whose address is proven undeliverable. <strong>Remove bad addresses</strong> suppresses the
            address, closes its marketing, archives the CRM lead as invalid_email (notes, emails and history are kept)
            and removes the marketing contact. If the person comes back with a new address, restore the archived lead
            and update its email.
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.bad_address_by_category).map(([cat, c]) => (
              <Chip key={cat} on={filters.bad_reason === cat}
                    onClick={() => setFilters({ ...filters, bad_reason: filters.bad_reason === cat ? undefined : cat })}
                    testid={`chip-bad-${cat}`}>
                {c.label} · {c.contacts.toLocaleString()}
              </Chip>
            ))}
          </div>
        </div>
      )}
      {filters.test_data === true && (
        <Banner tone="warn">
          Eyvo staff and test-domain contacts. <strong>Delete test data</strong> permanently deletes the contact and its
          test CRM lead (with its notes and activity). Staff addresses are never suppressed, so they keep receiving
          system email. Anything with a quote, project, client, ticket, meeting, reminder, booking or Sales stage is kept.
        </Banner>
      )}

      {activeState && (
        <div className="bg-white border border-[#E5E5E5] p-3 mb-3">
          <p className="text-xs text-[#525252] mb-2">{activeState.help}</p>
          <div className="flex flex-wrap gap-2">
            {activeState.reasons.map((r) => (
              <Chip key={r.reason_code} on={filters.reason === r.reason_code}
                    onClick={() => setFilters({ ...filters, reason: filters.reason === r.reason_code ? undefined : r.reason_code })}
                    testid={`chip-reason-${r.reason_code}`}>
                {r.reason} · {r.contacts.toLocaleString()}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {/* Search, sort */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <form onSubmit={(e) => { e.preventDefault(); setFilters({ ...filters, q: search || undefined }); }} className="flex items-center gap-1">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, company or email"
                   className="h-8 pl-6 pr-2 border border-[#E5E5E5] text-xs w-56" data-testid="audience-search" />
          </div>
        </form>
        <input defaultValue={filters.domain || ""} key={`d-${filters.domain || ""}`} placeholder="Domain"
               onBlur={(e) => setFilters({ ...filters, domain: e.target.value.trim() || undefined })}
               className="h-8 px-2 border border-[#E5E5E5] text-xs w-36" />
        <input defaultValue={filters.source || ""} key={`s-${filters.source || ""}`} placeholder="Lead source"
               onBlur={(e) => setFilters({ ...filters, source: e.target.value.trim() || undefined })}
               className="h-8 px-2 border border-[#E5E5E5] text-xs w-36" />
        <input defaultValue={filters.sales_stage || ""} key={`ss-${filters.sales_stage || ""}`} placeholder="Sales stage"
               onBlur={(e) => setFilters({ ...filters, sales_stage: e.target.value.trim() || undefined })}
               className="h-8 px-2 border border-[#E5E5E5] text-xs w-32" />
        <select value={filters.sort || "lead_created"} onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                className="h-8 px-2 border border-[#E5E5E5] text-xs" data-testid="audience-sort">
          {SORTS.map(([k, label]) => <option key={k} value={k}>Sort: {label}</option>)}
        </select>
        <select value={filters.dir || "asc"} onChange={(e) => setFilters({ ...filters, dir: e.target.value })}
                className="h-8 px-2 border border-[#E5E5E5] text-xs">
          <option value="asc">Oldest / A–Z first</option>
          <option value="desc">Newest / Z–A first</option>
        </select>
      </div>

      {filterWords.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-2 px-3 py-2 bg-[#FAFAFA] border border-[#E5E5E5] text-xs" data-testid="audience-active-filter">
          <span className="font-bold uppercase tracking-wider text-[10px] text-[#525252]">Filter</span>
          <span className="text-[#0A0A0A] font-semibold">{filterWords.join(" · ")}</span>
          <span className="text-[#525252]">
            — {loading ? "loading…" : `${list.total.toLocaleString()} current record${list.total === 1 ? "" : "s"}`}
          </span>
          {filters.held && (
            <span className="text-[#737373]">The Hold (technical) column shows why each enrolment is paused. Replied and stopped-by-Sales holds have no automatic release; DO NOT SEND and quarantine holds lift only by an explicit release.</span>
          )}
          <button onClick={() => setFilters({})} className="ml-auto text-[#0055FF] font-semibold" data-testid="audience-clear-filter">Clear filter</button>
        </div>
      )}

      {filters.cleanup === true && <StrandedPlan />}

      {/* Bulk bar */}
      <div className="flex flex-wrap items-center gap-2 mb-2 min-h-[36px]">
        <span className="text-xs text-[#525252]">
          {selected.length ? `${selected.length.toLocaleString()} selected · ${deletable.length.toLocaleString()} eligible for deletion` : `${list.total.toLocaleString()} contacts`}
        </span>
        {list.bad_address_removable_ids?.length > 0 && (filters.bad_address || filters.email) && (
          <button onClick={() => setSelected(Array.from(new Set([...selected, ...list.bad_address_removable_ids])))}
                  className="text-xs text-[#0055FF] font-semibold" data-testid="select-bad-removable">
            Select all {list.bad_address_removable_ids.length.toLocaleString()} removable bad addresses in this view
          </button>
        )}
        {list.test_data_deletable_ids?.length > 0 && filters.test_data && (
          <button onClick={() => setSelected(Array.from(new Set([...selected, ...list.test_data_deletable_ids])))}
                  className="text-xs text-[#0055FF] font-semibold" data-testid="select-test-data">
            Select all {list.test_data_deletable_ids.length.toLocaleString()} deletable test contacts in this view
          </button>
        )}
        {list.delete_eligible_ids.length > 0 && (
          <button onClick={() => setSelected(Array.from(new Set([...selected, ...list.delete_eligible_ids])))}
                  className="text-xs text-[#0055FF] font-semibold" data-testid="select-eligible">
            Select all {list.delete_eligible_ids.length.toLocaleString()} deletion-safe in this view
          </button>
        )}
        {selected.length > 0 && (
          <>
            <button onClick={() => setSelected([])} className="text-xs text-[#525252]">Clear selection</button>
            <span className="flex-1" />
            {Object.entries(OVERRIDE_LABELS).map(([kind, label]) => (
              <button key={kind} onClick={() => setOverrideAction({ kind, note: "", busy: false })}
                      className="h-8 px-3 border border-[#E5E5E5] bg-white text-xs font-semibold" data-testid={`bulk-${kind}`}>{label}</button>
            ))}
            {removableBad.length > 0 && (
              <button onClick={() => startAction("remove_bad")}
                      className="h-8 px-3 bg-[#B45309] hover:bg-[#92400E] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                      data-testid="bulk-remove-bad">
                <ProhibitInset size={12} /> Remove bad addresses ({removableBad.length})
              </button>
            )}
            {deletableTest.length > 0 && (
              <button onClick={() => startAction("test_data")}
                      className="h-8 px-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                      data-testid="bulk-delete-test">
                <Trash size={12} weight="bold" /> Delete test data ({deletableTest.length})
              </button>
            )}
            <button onClick={() => startAction("retire")}
                    className="h-8 px-3 border border-[#525252] bg-white text-[#0A0A0A] text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                    data-testid="bulk-retire">
              <ProhibitInset size={12} /> Retire from marketing
            </button>
            <button onClick={() => startAction("delete")} disabled={!deletable.length}
                    title={deletable.length ? "Permanently delete the selected deletion-safe contacts" : "None of the selected contacts is deletion-safe"}
                    className="h-8 px-3 bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-[#FCA5A5] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                    data-testid="bulk-delete">
              <Trash size={12} weight="bold" /> Delete garbage ({deletable.length})
            </button>
          </>
        )}
      </div>

      {/* Contacts */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        {listError ? <p className="p-6 text-sm text-[#DC2626]">{listError}</p>
          : loading && !list.rows.length ? <p className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</p>
          : !list.rows.length ? <p className="p-8 text-center text-sm text-[#A3A3A3]">No contacts match this view.</p>
          : (
            <table className="text-xs min-w-[2200px] w-full" data-testid="audience-table">
              <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                <tr>
                  <th className="px-3 py-2 w-8"><input type="checkbox" checked={allOnPageSelected} onChange={togglePage} aria-label="Select all on this page" /></th>
                  {["Contact", "Company", "Email", "State", "Why", "Lead source", "Lead created", "Last delivery",
                    "Last email attempt", "Last open", "Last click", "Last reply", "Sales stage", "Workflow", "Step",
                    "Suppression", "Hold (technical)", "Recommended action", "Safe to delete"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-bold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.rows.map((r) => (
                  <tr key={r.contact_id} className={`border-t border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer ${selected.includes(r.contact_id) ? "bg-[#EFF6FF]" : ""}`}
                      onClick={() => setDetailId(r.contact_id)} data-testid={`audience-row-${r.contact_id}`}>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.includes(r.contact_id)} onChange={() => toggle(r.contact_id)} aria-label={`Select ${r.email}`} />
                    </td>
                    <td className="px-3 py-2 font-semibold text-[#0A0A0A] whitespace-nowrap">{r.name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.company}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.email}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: STATE_COLORS[r.state] }}>{r.state_label}</span>
                      {r.conflict && <span title="Automatic email competes with Sales"><Warning size={12} className="inline ml-1 text-[#D97706]" /></span>}
                    </td>
                    <td className="px-3 py-2 min-w-[260px]">
                      <div className="font-semibold text-[#0A0A0A]">{r.reason}</div>
                      <div className="text-[#737373]">{r.detail}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.lead_source}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.lead_created}{r.lead_age_days != null ? ` (${Math.round(r.lead_age_days / 30)} mo)` : ""}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.last_delivery}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.last_attempt}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.last_open}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.last_click}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.last_reply}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.sales_stage}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.current_workflow}{r.next_send !== "None" ? ` · next ${r.next_send}` : ""}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.current_step}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.suppression}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[#A3A3A3]">{r.hold_reason}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.recommended_action}{r.verified_until ? ` · verified until ${r.verified_until}` : ""}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.delete_eligible
                        ? <span className="text-[#DC2626] font-bold" title={r.delete_reasons.join("; ")}>Yes</span>
                        : <span className="text-[#525252]" title={r.delete_blockers.join("; ") || "No garbage evidence"}>No</span>}
                      {/* In the cleanup view, say WHY in the row, not only on hover. */}
                      {filters.delete_eligible && r.delete_reasons?.length > 0 && (
                        <div className="text-[#737373] whitespace-normal min-w-[220px]">{r.delete_reasons.join("; ")}</div>
                      )}
                      {filters.bad_address && r.bad_address_evidence?.length > 0 && (
                        <div className="whitespace-normal min-w-[260px]" data-testid={`bad-evidence-${r.contact_id}`}>
                          <div className={r.bad_address_removable ? "text-[#B45309] font-semibold" : "text-[#525252] font-semibold"}>
                            {r.bad_address_removable ? "Removable bad address" : "Bad address — kept"}
                          </div>
                          <div className="text-[#737373]">{r.bad_address_evidence.join("; ")}</div>
                          {r.bad_address_blockers?.length > 0 && <div className="text-[#737373]">Kept: {r.bad_address_blockers.join("; ")}</div>}
                        </div>
                      )}
                      {filters.test_data && r.test_data && (
                        <div className="whitespace-normal min-w-[220px] text-[#737373]">
                          {r.test_data_deletable ? "Deletable test data" : `Kept: ${r.test_data_blockers.join("; ")}`}
                        </div>
                      )}
                      {/* Garbage evidence but kept: say what keeps it, in the row. */}
                      {!r.delete_eligible && r.delete_reasons?.length > 0 && r.delete_blockers?.length > 0 && (
                        <div className="text-[#737373] whitespace-normal min-w-[220px]" data-testid={`delete-blockers-${r.contact_id}`}>
                          Kept: {r.delete_blockers.join("; ")}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      {list.total > PAGE && (
        <div className="flex items-center justify-end gap-2 mt-2 text-xs">
          <span className="text-[#525252]">{offset + 1}–{Math.min(offset + PAGE, list.total)} of {list.total.toLocaleString()}</span>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="h-7 px-2 border border-[#E5E5E5] bg-white disabled:opacity-40">Previous</button>
          <button disabled={offset + PAGE >= list.total} onClick={() => setOffset(offset + PAGE)} className="h-7 px-2 border border-[#E5E5E5] bg-white disabled:opacity-40">Next</button>
        </div>
      )}

      {detailId && <DetailDrawer contactId={detailId} onClose={() => setDetailId(null)} />}
      {action && <ActionDialog action={action} setAction={setAction} onConfirm={confirmAction} />}
      {overrideAction && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !overrideAction.busy && setOverrideAction(null)}>
          <div className="bg-white border border-[#E5E5E5] p-6 w-full max-w-md text-xs" onClick={(e) => e.stopPropagation()} data-testid="override-dialog">
            <h3 className="text-lg font-black tracking-tight mb-2">{OVERRIDE_LABELS[overrideAction.kind]}</h3>
            <p className="text-[#525252] mb-3">{OVERRIDE_HELP[overrideAction.kind]} Applies to {selected.length.toLocaleString()} selected contact{selected.length !== 1 ? "s" : ""}.</p>
            {overrideAction.kind !== "clear" && (
              <textarea value={overrideAction.note} onChange={(e) => setOverrideAction({ ...overrideAction, note: e.target.value })}
                        placeholder="Why? (kept in the audit log)" rows={2} className="w-full px-2 py-1 border border-[#E5E5E5] mb-3" />
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setOverrideAction(null)} disabled={overrideAction.busy} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">Cancel</button>
              <button onClick={applyOverride} disabled={overrideAction.busy} className="h-9 px-4 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40" data-testid="override-confirm">
                {overrideAction.busy ? "Working…" : OVERRIDE_LABELS[overrideAction.kind]}
              </button>
            </div>
          </div>
        </div>
      )}
    </MarketingShell>
  );
}

// Why a paused row can never send (hold_release.dead_hold_reason codes).
const DEAD_REASON_LABELS = {
  past_final_step: "Already past the workflow's last step",
  address_suppressed: "Address is suppressed",
  lead_removed: "CRM lead was removed",
  booking_check_orphaned: "Booking check with no open prompt",
  manual_stop: "Stopped by Sales",
  no_crm_lead: "No CRM lead for this address",
  lead_ineligible: "CRM lead is no longer eligible",
};

/**
 * READ-ONLY closure plan for enrolment rows that can never send — what the
 * digest's "stranded enrolment" refers to. Shows why each row is stranded
 * and how it would close; it closes nothing.
 */
function StrandedPlan() {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    api.get("/marketing/holds/dead")
      .then((r) => { if (live) setPlan(r.data); })
      .catch((e) => { if (live) setError(errorText(e, "Could not load the stranded-enrolment plan.")); });
    return () => { live = false; };
  }, []);
  if (error) return <Banner tone="error">{error} The contacts below are still listed.</Banner>;
  if (!plan) return null;
  return (
    <div className="bg-white border border-[#E5E5E5] p-3 mb-3 text-xs" data-testid="stranded-plan">
      <p className="font-bold text-[#0A0A0A] mb-1">
        {plan.dead_rows.toLocaleString()} paused enrolment row{plan.dead_rows === 1 ? "" : "s"} can never send
        {plan.restricted_dead_rows > 0 && ` (${plan.restricted_dead_rows.toLocaleString()} under a DO NOT SEND or quarantine hold)`}
      </p>
      <p className="text-[#525252] mb-2">
        Nothing is sent to these rows; they only keep a contact looking “held”. Closing them is a reviewed,
        audited operator action (dry run first) and is not done from this screen — nothing here changes them.
      </p>
      {plan.rows.length > 0 && (
        <table className="w-full">
          <thead className="text-[10px] uppercase tracking-wider text-[#A3A3A3] text-left">
            <tr><th className="py-1 pr-3">Email</th><th className="pr-3">Workflow</th><th className="pr-3">Hold</th>
              <th className="pr-3">Why it can never send</th><th>Would close as</th></tr>
          </thead>
          <tbody>
            {plan.rows.map((r) => (
              <tr key={r.enrollment_id} className="border-t border-[#F5F5F5]">
                <td className="py-1 pr-3">{r.email_lower}</td>
                <td className="pr-3">{r.workflow}</td>
                <td className="pr-3">{r.pause_reason || "(unrecorded)"}{r.restricted ? " · restricted" : ""}</td>
                <td className="pr-3">{DEAD_REASON_LABELS[r.dead_reason] || r.dead_reason}</td>
                <td>{r.terminal_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function countOutcomes(results) {
  const out = {};
  (results || []).forEach((r) => { out[r.outcome] = (out[r.outcome] || 0) + 1; });
  return out;
}

function Banner({ tone, children }) {
  const c = tone === "error" ? "bg-[#FEF2F2] text-[#991B1B] border-[#FCA5A5]" : "bg-[#FFFBEB] text-[#92400E] border-[#FCD34D]";
  return <div className={`border px-3 py-2 text-xs mb-3 ${c}`}>{children}</div>;
}

function Chip({ on, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-2 py-1 border text-xs ${on ? "border-[#0055FF] bg-[#EFF6FF] text-[#0055FF]" : "border-[#E5E5E5] bg-white text-[#525252] hover:border-[#0055FF]"}`}>
      {children}
    </button>
  );
}

function DetailDrawer({ contactId, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    api.get(`/marketing/audience/contacts/${encodeURIComponent(contactId)}`)
      .then((r) => { if (!cancelled) setD(r.data); })
      .catch((e) => { if (!cancelled) setErr(errorText(e, "Could not load this contact's evidence.")); });
    return () => { cancelled = true; };
  }, [contactId]);
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto p-5 text-xs" onClick={(e) => e.stopPropagation()} data-testid="audience-drawer">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-sm font-black text-[#0A0A0A]">{d?.name || "Contact"}</p>
            <p className="text-[#525252]">{d?.email} · {d?.company}</p>
          </div>
          <button onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        {err && <Banner tone="error">{err}</Banner>}
        {!d && !err && <p className="text-[#A3A3A3]">Loading…</p>}
        {d && (
          <div className="space-y-4">
            <Section title="Classification">
              <p><span className="font-bold" style={{ color: STATE_COLORS[d.state] }}>{d.state_label}</span> — {d.reason}</p>
              <p className="text-[#525252]">{d.detail}</p>
              <p className="mt-1">Recommended: <span className="font-semibold">{d.recommended_action}</span></p>
              {d.override && <p className="mt-1 text-[#EA580C]">Operator override: {d.override.state} by {d.override.set_by}{d.override.note ? ` — ${d.override.note}` : ""}</p>}
            </Section>
            <Section title="Permanent deletion">
              {d.delete_eligible
                ? <p className="text-[#DC2626] font-semibold">Deletion-safe: {d.delete_reasons.join("; ")}</p>
                : <>
                    <p>Not deletion-safe.</p>
                    {d.delete_reasons.length > 0 && <p className="text-[#525252]">Garbage evidence: {d.delete_reasons.join("; ")}</p>}
                    {d.delete_blockers.length > 0 && <p className="text-[#525252]">Kept because: {d.delete_blockers.join("; ")}</p>}
                  </>}
            </Section>
            <Section title="Email evidence">
              <KV data={{
                "Sends": d.evidence.sends, "Sent during the May–Aug telemetry gap": d.evidence.sent_in_telemetry_gap,
                "Provider-confirmed deliveries": d.evidence.delivered_sends, "Last delivery": d.last_delivery,
                "Last email attempt": d.last_attempt, "Last open": d.last_open, "Last click": d.last_click,
                "Last reply": d.last_reply, "Permanent bounces": d.evidence.hard_failures,
                "Domain has no mail server": d.evidence.no_domain_failures, "Receiver blocks": d.evidence.receiver_blocks,
                "Unclassified failures": d.evidence.unclassified_failures, "Temporary failures": d.evidence.temporary_failures,
                "Last failure": d.evidence.last_failure, "Bookings": d.evidence.bookings, "Suppression": d.suppression,
              }} />
            </Section>
            <Section title="CRM leads">
              {d.leads.length === 0 ? <p className="text-[#A3A3A3]">No CRM lead for this address.</p> : (
                <table className="w-full"><tbody>
                  {d.leads.map((l) => (
                    <tr key={l.lead_id} className="border-t border-[#F5F5F5]">
                      <td className="py-1 pr-2 font-mono">{l.lead_id}</td><td className="pr-2">{l.status}</td>
                      <td className="pr-2">{l.source}</td><td className="pr-2">{l.created}</td>
                      <td>{l.deleted ? "deleted" : l.marketable ? "marketable" : "not marketable"}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}
            </Section>
            <Section title="Workflow enrolments">
              {d.enrolments.length === 0 ? <p className="text-[#A3A3A3]">Never enrolled.</p> : (
                <table className="w-full"><tbody>
                  {d.enrolments.map((e) => (
                    <tr key={e.id} className="border-t border-[#F5F5F5]">
                      <td className="py-1 pr-2">{e.workflow}</td><td className="pr-2">{e.status}</td>
                      <td className="pr-2">{e.step ? `step ${e.step}${e.steps ? ` of ${e.steps}` : ""}` : ""}</td>
                      <td className="pr-2 text-[#A3A3A3]">{e.hold_reason}</td>
                      <td>{e.cleanup ? <span className="text-[#D97706]">can never send ({e.cleanup})</span> : e.next_send !== "None" ? `next ${e.next_send}` : ""}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}
            </Section>
            <Section title="Business history (blocks permanent deletion)">
              <KV data={d.relationships} />
            </Section>
            <Section title="CRM timeline events">
              <KV data={Object.fromEntries(Object.entries(d.evidence.lead_events || {}).map(([k, v]) => [k, `${v.n} (last ${v.last || "?"})`]))} />
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">{title}</p>
      {children}
    </div>
  );
}

function KV({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return <p className="text-[#A3A3A3]">None.</p>;
  return (
    <table className="w-full"><tbody>
      {entries.map(([k, v]) => (
        <tr key={k} className="border-t border-[#F5F5F5]">
          <td className="py-1 pr-3 text-[#525252]">{k}</td>
          <td className="py-1 font-semibold">{v === null || v === undefined ? "Unavailable" : String(v)}</td>
        </tr>
      ))}
    </tbody></table>
  );
}

// The four bulk actions: dry-run outcome, done outcome, typed confirmation.
const ACTIONS = {
  delete: { preview: "would_delete", verb: "deleted", confirmWord: "DELETE", danger: true,
    title: "Permanently delete garbage contacts",
    warning: "Deletion is permanent. The marketing contact and its CRM lead records are removed and cannot be restored. "
      + "Delivery statistics, the suppression list and the audit log are kept. Each contact is re-checked on the "
      + "server immediately before it is deleted; any that are no longer safe are skipped." },
  retire: { preview: "would_retire", verb: "retired", confirmWord: null, danger: false,
    title: "Retire from marketing",
    warning: "These addresses will never receive marketing again. Live enrolments are cancelled. CRM leads, their "
      + "status, notes, proposals and history are kept, and Sales can still email them directly." },
  remove_bad: { preview: "would_remove", verb: "removed", confirmWord: "REMOVE", danger: true,
    title: "Remove bad addresses",
    warning: "Each address is added to the suppression list (it can never be emailed again), its marketing "
      + "enrolments are closed, the marketing contact is removed, and the CRM lead is ARCHIVED as invalid_email. "
      + "The lead's notes, emails, events, replies and engagement are kept, and the lead can be restored with a new "
      + "address. Each contact is re-checked on the server first; any that no longer qualify are skipped." },
  test_data: { preview: "would_delete", verb: "deleted", confirmWord: "DELETE", danger: true,
    title: "Delete Eyvo staff / test data",
    warning: "Deletion is permanent: the contact and its test CRM lead, with its notes and activity, are removed. "
      + "Staff addresses are NOT suppressed, so they keep receiving system email. Delivery statistics and the "
      + "audit log are kept." },
};

function ActionDialog({ action, setAction, onConfirm }) {
  const cfg = ACTIONS[action.kind];
  const isDelete = cfg.danger;
  const want = cfg.preview;
  const preview = action.preview?.results || [];
  const willAct = preview.filter((r) => r.outcome === want);
  const skipped = preview.filter((r) => r.outcome !== want);
  const reasonCounts = {};
  willAct.forEach((r) => [...(r.reasons || []), ...((r.categories || []).map((c) => BAD_ADDRESS_CATEGORIES[c] || c))]
    .forEach((x) => { reasonCounts[x] = (reasonCounts[x] || 0) + 1; }));
  const done = action.results;
  const canConfirm = !action.busy && !done && willAct.length > 0 && (!cfg.confirmWord || action.typed === cfg.confirmWord);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !action.busy && setAction(null)}>
      <div className={`bg-white border-2 p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto text-xs ${isDelete ? "border-[#DC2626]" : "border-[#525252]"}`}
           onClick={(e) => e.stopPropagation()} data-testid="audience-action-dialog">
        <h3 className={`text-lg font-black tracking-tight mb-2 flex items-center gap-2 ${isDelete ? "text-[#DC2626]" : "text-[#0A0A0A]"}`}>
          {isDelete ? <Trash size={18} weight="bold" /> : <ProhibitInset size={18} />} {cfg.title}
        </h3>
        {action.busy && !action.preview && <p className="text-[#525252]">Checking every selected contact (dry run, nothing is changed)…</p>}
        {action.preview && !done && (
          <>
            <Banner tone={isDelete ? "error" : "warn"}>{cfg.warning}</Banner>
            <p className="text-sm font-bold mb-1">
              {willAct.length.toLocaleString()} of {preview.length.toLocaleString()} selected contact{preview.length !== 1 ? "s" : ""} will be {cfg.verb}.
            </p>
            {Object.keys(reasonCounts).length > 0 && (
              <ul className="mb-2 list-disc pl-5">
                {Object.entries(reasonCounts).map(([k, v]) => <li key={k}>{v.toLocaleString()} — {k}</li>)}
              </ul>
            )}
            {skipped.length > 0 && (
              <details className="mb-2"><summary className="cursor-pointer">{skipped.length} will be skipped — why</summary>
                <ul className="pl-5 list-disc">
                  {skipped.slice(0, 200).map((r) => (
                    <li key={r.contact_id}>{r.email || r.contact_id}: {(r.blockers || []).join("; ") || r.reason || r.outcome}</li>
                  ))}
                </ul>
              </details>
            )}
            <details className="mb-3"><summary className="cursor-pointer">List the {willAct.length} contacts</summary>
              <ul className="pl-5 list-disc">{willAct.map((r) => <li key={r.contact_id}>{r.email}</li>)}</ul>
            </details>
            <textarea value={action.note} onChange={(e) => setAction({ ...action, note: e.target.value })}
                      placeholder="Reason for the audit log (optional)" rows={2}
                      className="w-full px-2 py-1 border border-[#E5E5E5] mb-2" />
            {cfg.confirmWord && (
              <input value={action.typed} onChange={(e) => setAction({ ...action, typed: e.target.value })}
                     placeholder={`Type ${cfg.confirmWord} to confirm`} className="w-full h-9 px-2 border border-[#DC2626] mb-3"
                     data-testid="delete-confirm-input" />
            )}
          </>
        )}
        {done && (
          <>
            {done.error && (
              <Banner tone="error">
                Stopped part-way: {done.error} {done.notProcessed} contact(s) were not processed and are unchanged.
              </Banner>
            )}
            <p className="text-sm font-bold mb-2">{outcomeSummary(done.outcomes) || "No contact was changed."}</p>
            <ul className="pl-5 list-disc max-h-64 overflow-y-auto">
              {done.results.map((r) => (
                <li key={r.contact_id}>{r.email || r.contact_id}: {r.outcome}{r.blockers?.length ? ` — ${r.blockers.join("; ")}` : ""}{r.detail ? ` — ${r.detail}` : ""}</li>
              ))}
            </ul>
          </>
        )}
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => setAction(null)} disabled={action.busy}
                  className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">{done ? "Close" : "Cancel"}</button>
          {!done && (
            <button onClick={onConfirm} disabled={!canConfirm}
                    className={`h-9 px-4 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 ${isDelete ? "bg-[#DC2626]" : "bg-[#0A0A0A]"}`}
                    data-testid="audience-action-confirm">
              {action.busy ? "Working…" : {
                delete: `Delete ${willAct.length} permanently`, retire: `Retire ${willAct.length}`,
                remove_bad: `Remove ${willAct.length} bad address${willAct.length === 1 ? "" : "es"}`,
                test_data: `Delete ${willAct.length} test contact${willAct.length === 1 ? "" : "s"} permanently`,
              }[action.kind]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
